/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  VStack,
  useNumberInput,
} from '@chakra-ui/react';
import { FaMinus, FaPlus } from 'react-icons/fa6';
import { useEffect, useMemo } from 'react';

import AssignmentBuilderFormContainer from '../layout/AssignmentBuilderFormContainer';
import AssignmentDueDateForm from './settings/AssignmentDueDateForm';
import AssignmentEscalationContactsForm from './settings/AssignmentEscalationContactsForm';
import type { AssignmentFormData } from '../../types/assignmentTypes';
import CreateSwitchContainer from '../../../create/shared/layout/CreateSwitchContainer';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AssignmentSettingsForm() {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;

  const isInstanceEdit = pathname.includes('edit/instance');

  const { setActiveForm, setSettingsFormVisited } = useAdminAssignmentsStore();
  const {
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<AssignmentFormData>();

  useEffect(() => {
    setActiveForm('settings');
    setSettingsFormVisited(true);
  }, []);

  const { formBoundType, useAssignmentDelay, settings } = watch();

  const { assignmentOptions, responses } = settings;

  const { delay } = assignmentOptions;

  const hasError = !!errors?.customDueDate || !!errors?.settings?.daysToComplete;

  const disableRecompletion = useMemo(() => {
    const hasNonRecompletable = responses?.some(
      (response) => response.content?.isRecompletable === 'false',
    );

    return hasNonRecompletable;
  }, [responses]);

  const options = useMemo(
    () => [
      {
        name: 'settings.requireRecompletion',
        label: t('admin.assignments.settings.uniqueCompletions'),
        helpText: t('admin.assignments.settings.uniqueCompletions.helpText'),
        render: !disableRecompletion && !isInstanceEdit,
      },
      {
        name: 'settings.escalateManager',
        label: t('admin.assignments.settings.escalateManager'),
        helpText: t('admin.assignments.settings.escalateManager.helpText'),
        render: true,
      },
      {
        name: 'settings.escalateBuddy',
        label: t('admin.assignments.settings.escalateBuddy'),
        helpText: t('admin.assignments.settings.escalateBuddy.helpText'),
        render: true,
      },
    ],
    [disableRecompletion, isInstanceEdit],
  );

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } = useNumberInput({
    defaultValue: delay || 0,
    min: 0,
    max: 10000,
  });

  const incDays = getIncrementButtonProps();
  const decDays = getDecrementButtonProps();
  const inputDays = getInputProps();

  useEffect(() => {
    if (inputDays.value !== delay) {
      setValue('settings.assignmentOptions.delay', inputDays.value);
    }
  }, [inputDays.value]);

  return (
    <AssignmentBuilderFormContainer
      headerComponent={
        <Flex justifyContent="space-between" alignItems="center">
          <Stack spacing={0}>
            <Text fontSize="24px" fontWeight={600}>
              {t('admin.assignments.settings.header')}
            </Text>

            {hasError && (
              <Text color="error" fontSize="14px" fontWeight={400}>
                {t('admin.assignments.settings.error')}
              </Text>
            )}
          </Stack>
        </Flex>
      }
      formComponent={
        <Stack>
          <AssignmentDueDateForm />

          {options.map((option) => {
            if (!option.render) return null;

            return (
              <Box
                key={option.name}
                paddingY={4}
                borderBottom="1px solid"
                borderBottomColor="neutral.100"
              >
                <CreateSwitchContainer
                  name={option.name}
                  label={option.label}
                  helpText={option.helpText}
                />
              </Box>
            );
          })}

          {formBoundType === WorkflowType.CompletedAssignment && (
            <Box paddingY={4} borderBottom="1px solid" borderBottomColor="neutral.100">
              <CreateSwitchContainer
                name="useAssignmentDelay"
                label={t('admin.assignments.settings.delayAssignment')}
                helpText={t('admin.assignments.settings.delayAssignment.helpText')}
              />

              <Collapse in={useAssignmentDelay} animateOpacity>
                <Flex justifyContent="flex-start" alignItems="center" marginTop={4}>
                  <VStack>
                    <HStack>
                      <IconButton
                        {...decDays}
                        size="sm"
                        variant="createNumberStepper"
                        isRound
                        icon={<FaMinus />}
                        aria-label={t('admin.assignments.audience.additionalFilters.decreaseDays')}
                      />

                      <Box width={16}>
                        <Input
                          {...inputDays}
                          variant="createNumber"
                          fontSize="14px"
                          fontWeight={600}
                        />
                      </Box>

                      <IconButton
                        {...incDays}
                        size="sm"
                        variant="createNumberStepper"
                        isRound
                        icon={<FaPlus />}
                        aria-label={t('admin.assignments.audience.additionalFilters.increaseDays')}
                      />
                    </HStack>

                    <Text variant="createHelpText">
                      {t('admin.assignments.settings.delayAssignment.days')}
                    </Text>
                  </VStack>
                </Flex>
              </Collapse>
            </Box>
          )}

          <AssignmentEscalationContactsForm />
        </Stack>
      }
    />
  );
}
